import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { GeoApi } from "../api/services";

export type Place = { lat: number; lng: number; address: string };

type Props = { value: Place | null; onSelect: (p: Place) => void; onType: (text: string) => void; placeholder: string; testId: string; loading?: boolean };

export function AddressSearch({ value, onSelect, onType, placeholder, testId, loading }: Props) {
  const [text, setText] = useState(value?.address || "");
  const [results, setResults] = useState<Place[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const typed = useRef(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => { setText(value?.address || ""); }, [value?.address]);
  useEffect(() => {
    if (!typed.current || text.trim().length < 3) { setResults([]); return; }
    const t = setTimeout(async () => {
      setBusy(true); setErr("");
      try { const r = await GeoApi.search(text); setResults(r.results); setOpen(true); box.current?.scrollIntoView({ block: "start", behavior: "smooth" }); }
      catch { setErr("Address search unavailable — tap the map instead"); }
      finally { setBusy(false); }
    }, 450);
    return () => clearTimeout(t);
  }, [text]);

  const pick = (p: Place) => { typed.current = false; setText(p.address); setOpen(false); setResults([]); onSelect(p); };

  return (
    <div className="relative flex-1 scroll-mt-4" ref={box}>
      <input className="fp-input h-11 pr-9" placeholder={placeholder} value={text} data-testid={testId}
        onChange={(e) => { typed.current = true; setText(e.target.value); onType(e.target.value); }}
        onFocus={() => results.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {(busy || loading) && <Loader2 size={16} className="absolute right-3 top-3.5 animate-spin text-slate-400" />}
      {open && results.length > 0 && (
        <ul className="absolute left-0 right-0 top-12 z-[500] max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg" data-testid={`${testId}-suggestions`}>
          {results.map((r, i) => (
            <li key={`${r.lat},${r.lng},${i}`}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(r)} data-testid={`${testId}-suggestion-${i}`}
                className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm text-slate-800 hover:bg-[#F1F9F4]">
                <MapPin size={14} className="mt-0.5 shrink-0 text-[#036248]" /><span className="line-clamp-2">{r.address}</span>
              </button>
            </li>
          ))}
          <li className="px-3 py-1.5 text-[10px] text-slate-400">Results © OpenStreetMap contributors</li>
        </ul>
      )}
      {open && !busy && typed.current && text.trim().length >= 3 && results.length === 0 && !err && (
        <p className="absolute left-0 right-0 top-12 z-[500] rounded-xl bg-white p-3 text-xs text-slate-500 shadow" data-testid={`${testId}-no-results`}>No matches — try a landmark or tap the map.</p>
      )}
      {err && <p className="mt-1 text-xs text-amber-800" data-testid={`${testId}-error`}>{err}</p>}
    </div>
  );
}
